import { programs } from '~api';
import { Program } from '~models';
import i18n from '../i18n';
import { getField, updateField } from 'vuex-map-fields';
import { Handler } from '~services';
import router from '~router';

const ADD_EMAIL = 'ADD_EMAIL';
const DELETE_EMAIL = 'DELETE_EMAIL';
const SET_PROGRAM = 'SET_PROGRAM';
const SET_PROGRAMS = 'SET_PROGRAMS';
const RESET_PROGRAM = 'RESET_PROGRAM';
const LOADING = 'LOADING';
const TRIGGER_MODAL = 'TRIGGER_MODAL';

const state = {
    program: { ...new Program() },
    programs: [],
    showModal: false,
    isLoading: false
};

const actions = {
    /**
     * Reset program and email state, redirect to programs list
     * @param {function} commit
     */
    cancel({ commit }) {
        commit(RESET_PROGRAM);
        router.push({ name: 'dashboard.programs' });
    },

    /**
     * Reset program state
     * @param {function} commit
     */
    resetProgram({ commit }) {
        commit(RESET_PROGRAM);
    },

    /**
     * Get programs
     * @param {function} commit
     * @returns {Promise<*>}
     */
    async getPrograms({ commit }) {
        const resp = await programs.list();

        if (resp.ok) {
            return commit(SET_PROGRAMS, resp.data);
        }

        return Handler.handleResponse(resp);
    },

    /**
     * Get programs
     * @param {function} commit
     * @returns {Promise<*>}
     */
    async getFlatPrograms({ commit }) {
        const params = {
            flat: true
        };
        const resp = await programs.list(params);

        if (resp.ok) {
            const items = resp.data.map(({ id, title }) => ({ id, title }));
            return commit(SET_PROGRAMS, items);
        }

        return Handler.handleResponse(resp);
    },

    /**
     * Create Program
     * @param {function} commit
     * @param {object} state
     */
    async create({ commit, state }) {
        const successMessage = i18n.t('programs.create.successMessage');

        let { title, sessions, emails } = state.program;

        sessions = sessions.map(item => item.id);

        commit(LOADING);

        // Api request to create business
        const resp = await programs.create({ title, sessions, emails });

        if (resp.ok) {
            commit(RESET_PROGRAM);
        }

        // Handle response
        Handler.handleResponse(resp, successMessage, '', 'dashboard.programs');

        commit(LOADING);
    },

    /**
     * Add email to program email list
     * @param {function} commit
     */
    async addEmail({ commit }, params) {
        await commit(ADD_EMAIL, params);
    },

    /**
     * Delete email to program email list
     * @param {function} commit
     */
    async deleteEmail({ commit }, index) {
        await commit(DELETE_EMAIL, index);
    },

    /**
     * Get Program
     * @param {function} commit
     * @param  {string} id
     */
    async read({ commit }, id) {
        commit(LOADING);
        // Api request get business account by id
        const resp = await programs.read(id);

        if (resp.ok) {
            commit(SET_PROGRAM, resp.data);
        } else {
            // Handle response if error
            Handler.handleResponse(resp);
        }

        commit(LOADING);

        return resp.ok;
    },

    /**
     * Update Business account
     * @param {function} commit
     * @param params {object} params
     * @returns {Promise<any>}
     */
    async update({ commit, state }) {
        const successMessage = i18n.t('programs.edit.successMessage');

        let { id, sessions, ...rest } = state.program;

        sessions = sessions.map(({ id }) => id);

        commit(LOADING);

        const resp = await programs.update(id, { sessions, ...rest });

        Handler.handleResponse(resp, successMessage, '', 'dashboard.programs');

        // Reset store business if account deleted
        if (resp.ok) {
            commit(RESET_PROGRAM);
        }

        commit(LOADING);

        return resp.ok;
    },

    /**
     * Delete Program.
     * @param {function} commit
     * @param {function} dispatch
     * @param {object} state
     * @returns {Promise<void>}
     */
    async deleteProgram({ commit, dispatch }, id) {
        let redirectName = 'dashboard.programs';

        const successMessage = i18n.t('programs.list.deleteSuccessMessage');

        // Show confirm modal window
        const confirmed = await dispatch(
            'prompt/show',
            i18n.t('prompt.delete'),
            { root: true }
        );
        if (!confirmed) return;

        commit(LOADING);

        // Api request on delete account
        const resp = await programs.delete(id);

        // Handle response
        Handler.handleResponse(resp, successMessage, '', redirectName);

        // Reset store business if account deleted
        if (resp.ok) {
            commit(RESET_PROGRAM);
        }

        commit(LOADING);

        return resp.ok;
    }
};

const mutations = {
    // Add the `updateField` mutation to the
    // `mutations` of your Vuex store instance.
    updateField,
    [ADD_EMAIL](state, params) {
        if (params.index === -1) {
            state.program.emails = [...state.program.emails, params.data];
        } else {
            state.program.emails[params.index] = params.data;
        }
    },
    [DELETE_EMAIL](state, index) {
        if (index > -1) {
            state.program.emails.splice(index, 1);
        }
    },
    [SET_PROGRAM](state, data) {
        let { id, title, sessions, emails } = data;

        emails = emails.map(({ subject, title, message }) => ({
            subject,
            title,
            message
        }));

        state.program = {
            id,
            title,
            sessions,
            emails
        };
    },
    [SET_PROGRAMS](state, data) {
        state.programs = data;
    },
    [RESET_PROGRAM](state) {
        state.program = { ...new Program() };
    },
    [LOADING](state) {
        state.isLoading = !state.isLoading;
    },
    [TRIGGER_MODAL](state) {
        state.showModal = !state.showModal;
    }
};

const getters = {
    // Add the `getField` getter to the
    // `getters` of your Vuex store instance.
    getField,
    isLoading: state => state.isLoading,
    emails: state => state.program.emails,
    programs: state => state.programs,
    program: state => state.program,
    emailIndex: state => state.emailIndex
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
};
